import { useMemo, useState } from 'react';
import {
  useGetCCAApplicantResultQuery,
  useGetCCAResultsQuery,
} from '../../../../../../../utils/api_service/ccaApi';
import { useGetJobApplicantsQuery } from '../../../../../../../utils/api_service/jobOpeningsApi';
import { CCAQuestionResult, CCAResultSummary } from '../../../../../../../utils/types/cca_types';

/** Groups responses by subdomain and returns average score (0–100) per subdomain. */
export const computeSubdomainScores = (
  questionResults: CCAQuestionResult[] | null | undefined,
): Record<string, number> => {
  const groups: Record<string, { sum: number; count: number }> = {};
  for (const qr of questionResults ?? []) {
    const key = qr.subdomain;
    if (!groups[key]) groups[key] = { sum: 0, count: 0 };
    const score = qr.aiScore != null
      ? qr.aiScore
      : qr.isCorrect === true ? 100 : 0;
    groups[key].sum += score;
    groups[key].count += 1;
  }
  const result: Record<string, number> = {};
  for (const [sd, { sum, count }] of Object.entries(groups)) {
    result[sd] = count > 0 ? Math.round(sum / count) : 0;
  }
  return result;
};

export interface ApplicantProfile {
  firstName: string;
  lastName: string;
  email: string;
}

export type EnrichedResultSummary = CCAResultSummary & ApplicantProfile;

export const useCCAResults = (jobOpeningId: string) => {
  const [selectedApplicantId, setSelectedApplicantId] = useState<string | null>(null);

  const {
    data: resultsData,
    isLoading: isListLoading,
    isError: isListError,
  } = useGetCCAResultsQuery(jobOpeningId, { skip: !jobOpeningId });

  const { data: applicantsData } = useGetJobApplicantsQuery(
    { id: jobOpeningId, page: 1, limit: 200, search: '', status: '' },
    { skip: !jobOpeningId },
  );

  const profileMap = useMemo(() => {
    const map = new Map<string, ApplicantProfile>();
    for (const a of applicantsData?.jobApplicants ?? []) {
      map.set(a.id ?? '', {
        firstName: a.firstName ?? '',
        lastName: a.lastName ?? '',
        email: a.email ?? a.contactInfo?.email ?? '',
      });
    }
    return map;
  }, [applicantsData]);

  const results: EnrichedResultSummary[] = useMemo(() =>
    (resultsData?.results ?? []).map((r) => {
      const profile = profileMap.get(r.applicantId);
      return {
        ...r,
        firstName: profile?.firstName ?? '',
        lastName: profile?.lastName ?? '',
        email: profile?.email ?? '',
      };
    }),
  [resultsData, profileMap],
  );

  const {
    data: applicantResult,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useGetCCAApplicantResultQuery(
    { jobOpeningId, applicantId: selectedApplicantId! },
    { skip: !jobOpeningId || !selectedApplicantId },
  );

  const selectApplicant = (id: string) =>
    setSelectedApplicantId((prev) => (prev === id ? null : id));

  return {
    results,
    total: results.length,
    isListLoading,
    isListError,
    selectedApplicantId,
    selectApplicant,
    applicantResult,
    isDetailLoading,
    isDetailError,
  };
};
